import { useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, Cross1Icon } from '@radix-ui/react-icons';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import Select, { GroupBase, StylesConfig } from 'react-select';
import { SelectStyles } from '@/features/plants/components/plant-management/shared/select-styles.ts';
import { Button } from '@/components/ui/button.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { TUserWithAccessControl } from '@/features/plants/components/plant-management/user-access/plant-user-access-management.tsx';

const USERS_PER_PAGE = 5;

export const PlantUserAccessManagementMobile = ({
  users,
  setUsers,
  isLoading,
}: {
  users: TUserWithAccessControl[];
  setUsers: React.Dispatch<React.SetStateAction<TUserWithAccessControl[]>>;
  isLoading: boolean;
}) => {
  const availableUsers = users
    ?.filter((u) => u.columnId === 'available-users')
    ?.map((u) => ({
      value: u.userId,
      label: `${u.firstName} ${u.lastName} - ${u.role}`,
    }));
  const associatedUsers = users
    .filter((u) => u.columnId === 'associated-users')
    .sort((a, b) => {
      if (a.newlyAdded && !b.newlyAdded) return -1;
      if (!a.newlyAdded && b.newlyAdded) return 1;
      return 0;
    });

  const [currentPage, setCurrentPage] = useState(1);
  const indexOfLastUser = currentPage * USERS_PER_PAGE;
  const indexOfFirstUser = indexOfLastUser - USERS_PER_PAGE;
  const currentUsers = associatedUsers.slice(indexOfFirstUser, indexOfLastUser);
  const numOfPages = associatedUsers?.length
    ? Math.trunc((associatedUsers.length - 1) / USERS_PER_PAGE) + 1
    : 1;

  const selectValue: { label: string; value: string } = {
    label: availableUsers?.length ? 'Assign a basic' : 'All available users are assigned',
    value: 'select',
  };

  if (isLoading)
    return (
      <div className="flex min-h-[332px] w-full flex-col">
        <Skeleton className="h-9" />

        <div className="mt-4 flex flex-col gap-2">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
        <div className="mt-4 flex h-full items-center justify-between">
          <Skeleton className="h-5 w-16" />

          <div className="flex gap-2">
            <Skeleton className="size-8" />
            <Skeleton className="size-8" />
          </div>
        </div>
      </div>
    );

  return (
    <div className="mt-4 flex min-h-[332px] flex-col justify-between">
      <div>
        <Select
          value={selectValue}
          isDisabled={!availableUsers?.length}
          isSearchable={false}
          options={availableUsers}
          placeholder="Assign a user"
          onChange={(selectedOption) => {
            const option = selectedOption as {
              value: string;
              label: string;
            } | null;

            if (option) {
              const userId = option.value;

              if (userId) {
                setUsers((current) =>
                  current?.map((u) => {
                    if (u.userId === userId)
                      return {
                        ...u,
                        columnId: 'associated-users',
                        newlyAdded: true,
                      };

                    return u;
                  })
                );
              }
            }
          }}
          styles={{
            ...(SelectStyles as StylesConfig<
              { label: string; value: string },
              boolean,
              GroupBase<{ label: string; value: string }>
            >),
            menu: (styles) => ({
              ...styles,
              ...SelectStyles.menu,
              maxHeight: '200px',
              overflowY: 'scroll',
            }),
          }}
        />

        <ScrollArea>
          <ul className="mt-4 flex max-h-[260px] flex-col gap-2">
            {currentUsers.map((user) => {
              const role = user.role.split(' ');
              const roleLabel =
                role.length === 1
                  ? user.role
                  : role.length === 2
                    ? `${role?.[0]?.[0]?.toLocaleUpperCase()} ${role?.[1]?.[0]?.toLocaleUpperCase()}${role?.[1]?.slice(1)}`
                    : '';
              return (
                <li
                  key={user.userId}
                  className="flex justify-between rounded-lg bg-slate-100 px-4 py-2 aria-disabled:opacity-50 aria-disabled:hover:ring-0"
                >
                  <div>
                    <span className="inline-block">
                      {user.firstName} {user.lastName}
                    </span>{' '}
                    - <span className="inline-block">{roleLabel}</span>{' '}
                    {user.newlyAdded && (
                      <span className="inline-block size-2 rounded-full bg-blue-400"></span>
                    )}
                  </div>
                  <div></div>
                  <button
                    onClick={() => {
                      setUsers((current) =>
                        current?.map((u) => {
                          if (u.userId === user.userId)
                            return {
                              ...u,
                              columnId: 'available-users',
                            };

                          return u;
                        })
                      );

                      if (currentUsers.length % 5 === 1) {
                        setCurrentPage((currentPage) =>
                          currentPage === 1 ? currentPage : currentPage - 1
                        );
                      }
                    }}
                    className="hover:ring-primary p-1 hover:ring-2 hover:ring-inset"
                  >
                    <Cross1Icon />
                  </button>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center">
          <div className="flex items-center justify-center text-sm font-medium sm:w-[100px]">
            Page {currentPage} of {numOfPages}
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (currentPage <= 1) return;
                setCurrentPage((current) => current - 1);
              }}
              disabled={currentPage <= 1}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (currentPage >= numOfPages) return;
                setCurrentPage((current) => current + 1);
              }}
              disabled={currentPage >= numOfPages}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRightIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
